import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Terminal, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function CliAuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [userCode, setUserCode] = useState(searchParams.get("code") || "");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleApprove = async (approve: boolean) => {
    if (!userCode.trim()) {
      setErrorMessage("Please enter a valid code");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      // Refresh session to ensure fresh JWT token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.refreshSession();

      if (sessionError || !session) {
        toast({
          title: "Authentication required",
          description: "Please log in to approve CLI access",
          variant: "destructive",
        });
        navigate("/login?redirect=/cli-auth");
        return;
      }

      // Call approve-device-auth Edge Function
      const { data, error } = await supabase.functions.invoke("approve-device-auth", {
        body: {
          user_code: userCode.trim().toUpperCase(),
          approve,
        },
      });

      if (error) {
        console.error("Approval error:", error);
        setStatus("error");
        setErrorMessage(error.message || "Failed to process authorization");
        return;
      }

      if (!data.success) {
        setStatus("error");
        setErrorMessage(data.error || data.message || "Authorization failed");
        return;
      }

      // Success!
      setStatus("success");

      toast({
        title: approve ? "✅ Authorization approved" : "❌ Authorization denied",
        description: data.message,
        variant: approve ? "default" : "destructive",
      });
    } catch (error) {
      console.error("Error approving device auth:", error);
      setStatus("error");
      const errorMsg = error instanceof Error ? error.message : "An unexpected error occurred";
      setErrorMessage(errorMsg);

      toast({
        title: "Error",
        description: "Failed to process authorization request",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Terminal className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">CLI Authorization</h1>
          <p className="text-muted-foreground">Authorize LocalDocs CLI access to your account</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Enter Authorization Code</CardTitle>
            <CardDescription>Enter the code displayed in your terminal</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === "idle" && (
              <>
                <div className="space-y-2">
                  <Input
                    placeholder="ABC-123"
                    value={userCode}
                    onChange={(e) => {
                      setUserCode(e.target.value.toUpperCase());
                      setErrorMessage("");
                    }}
                    maxLength={7}
                    className="text-center text-lg font-mono tracking-wider"
                    disabled={loading}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !loading) {
                        handleApprove(true);
                      }
                    }}
                  />
                  {errorMessage && (
                    <Alert variant="destructive">
                      <XCircle className="h-4 w-4" />
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleApprove(true)}
                    disabled={loading || !userCode.trim()}
                    className="flex-1"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Approve
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleApprove(false)}
                    disabled={loading || !userCode.trim()}
                    variant="outline"
                    className="flex-1"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <XCircle className="mr-2 h-4 w-4" />
                        Deny
                      </>
                    )}
                  </Button>
                </div>

                <Alert>
                  <Terminal className="h-4 w-4" />
                  <AlertDescription>
                    This will grant your CLI tool access to your LocalDocs account for 30 days.
                  </AlertDescription>
                </Alert>
              </>
            )}

            {status === "success" && (
              <div className="text-center space-y-4 py-6">
                <div className="flex justify-center">
                  <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">Authorization Successful!</h3>
                  <p className="text-sm text-muted-foreground">
                    You can now return to your terminal. The CLI has been authorized.
                  </p>
                </div>
                <Button onClick={() => navigate("/dashboard")} variant="outline">
                  Return to Dashboard
                </Button>
              </div>
            )}

            {status === "error" && (
              <div className="text-center space-y-4 py-6">
                <div className="flex justify-center">
                  <div className="h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center">
                    <XCircle className="h-8 w-8 text-red-500" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">Authorization Failed</h3>
                  <p className="text-sm text-muted-foreground">{errorMessage}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setStatus("idle");
                      setUserCode("");
                      setErrorMessage("");
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Try Again
                  </Button>
                  <Button onClick={() => navigate("/dashboard")} variant="ghost" className="flex-1">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center text-sm text-muted-foreground">
          <p>
            Don't have a code? Run{" "}
            <code className="bg-muted px-1.5 py-0.5 rounded">localdocs login</code> in your
            terminal.
          </p>
        </div>
      </div>
    </div>
  );
}
